import { Link } from "react-router-dom";
import { Building2, CalendarClock, Inbox, PhoneCall, Users } from "lucide-react";
import { useLoad, useApi, fmtTime } from "@/lib/api";
import { FlagsPanel } from "@/components/p4/shared";
import { PageHead, card, ResultBadge, RecordBadge, Empty } from "@/components/admin/kit";

const Stat = ({ icon: Icon, label, value, testid, accent }) => (
  <div className={`${card} flex items-center gap-4`} data-testid={testid}>
    <div className={`grid h-12 w-12 place-items-center rounded-2xl ${accent ? "bg-[var(--orange)] text-white" : "bg-[var(--lav-2)] text-[var(--violet)]"}`}><Icon size={20} /></div>
    <div><p className="text-xs text-muted-foreground">{label}</p><p className="text-2xl font-extrabold text-[var(--ink)]">{value ?? "—"}</p></div>
  </div>
);

const Ring = ({ pct }) => {
  const r = 52, c = 2 * Math.PI * r, v = pct ?? 0;
  return (
    <svg viewBox="0 0 120 120" className="h-36 w-36" data-testid="dose-rate-ring">
      <circle cx="60" cy="60" r={r} fill="none" stroke="var(--lav-2)" strokeWidth="12" />
      <circle cx="60" cy="60" r={r} fill="none" stroke="var(--orange)" strokeWidth="12" strokeLinecap="round" strokeDasharray={`${(v / 100) * c} ${c}`} transform="rotate(-90 60 60)" />
      <text x="60" y="66" textAnchor="middle" className="fill-[var(--ink)] text-2xl font-extrabold">{pct == null ? "—" : `${pct}%`}</text>
    </svg>
  );
};

export default function Dashboard() {
  const [d] = useLoad("/dashboard");
  const api = useApi();
  return (
    <div data-testid="dashboard-page">
      <PageHead title="Dashboard" sub="Demo numbers include seeded sample calls from the last 7 days." />
      <div className="mb-4"><FlagsPanel api={api} path="/admin/flags4" seenPath={(id) => `/admin/flags4/${id}/seen`} testid="admin-flags-panel" /></div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Stat icon={Building2} label="Hospitals" value={d?.hospitals} testid="stat-hospitals" />
        <Stat icon={Users} label="Patients" value={d?.patients} testid="stat-patients" />
        <Stat icon={PhoneCall} label="Calls today" value={d ? `${d.calls_today}${d.calls_today_demo || d.calls_today_test ? ` (${[d.calls_today_demo && `${d.calls_today_demo} demo`, d.calls_today_test && `${d.calls_today_test} test`].filter(Boolean).join(", ")})` : ""}` : null} testid="stat-calls-today" />
        <Stat icon={CalendarClock} label="Refills due (7 days)" value={d?.refills_due} testid="stat-refills" accent />
        <Stat icon={Inbox} label="Demo requests (new)" value={d ? `${d.demo_requests_new} / ${d.demo_requests}` : null} testid="stat-demo-requests" />
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-[280px_1fr]">
        <div className={`${card} grid justify-items-center`} data-testid="stat-answered-rate">
          <p className="justify-self-start text-sm font-bold">Calls answered · 7 days</p>
          <Ring pct={d?.week?.answered_pct} />
          <p className="text-xs text-muted-foreground" data-testid="stat-delivery">{d ? `${d.week.delivered} of ${d.week.calls} reminders delivered · ${d.week.listened_pct ?? "—"}% listened fully` : ""}</p>
        </div>
        <div className={card}>
          <div className="mb-3 flex items-center"><p className="font-bold">Latest calls</p><Link to="/admin/call-log" className="ml-auto text-xs font-semibold text-[var(--violet)]">All calls</Link></div>
          {!d?.recent_calls?.length ? <Empty>No calls yet</Empty> : (
            <ul className="divide-y divide-[var(--lav-2)]">
              {d.recent_calls.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center gap-2 py-2.5 text-sm">
                  <span className="font-semibold">{c.patient_name}</span>{!c.demo && <RecordBadge type={c.record_type} />}
                  {(c.demo || c.test_call) && <span className={`rounded-full px-2 text-[11px] font-bold text-white ${c.demo ? "bg-red-600" : "bg-amber-600"}`} data-testid={`call-label-${c.id}`}>{c.demo ? "DEMO" : "TEST"}</span>}
                  <span className="text-muted-foreground">{c.to_masked} · {c.slot || ""} · {c.kind}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{fmtTime(c.called_at)}</span><ResultBadge r={c.result} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className={`${card} mt-4`}>
        <div className="mb-3 flex items-center"><p className="font-bold">Newest demo requests</p><Link to="/admin/demo-requests" className="ml-auto text-xs font-semibold text-[var(--violet)]">Open</Link></div>
        {!d?.recent_requests?.length ? <Empty>No demo requests yet. They appear here from the website form.</Empty> : d.recent_requests.map((r) => (
          <div key={r.id} className="flex flex-wrap gap-2 border-b border-[var(--lav-2)] py-2 text-sm last:border-0"><b>{r.org_name}</b><span className="text-muted-foreground">{r.name} · {r.town}</span><span className="ml-auto text-xs">{r.status}</span></div>
        ))}
      </div>
    </div>
  );
}
